/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-29-10 08:00:00
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Pagination, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import TableHeadingCell from './table-heading-cell';
import Percent from 'src/components/views/analytics/percent';
import { NUMBER_FILTER_OPTION_VALUES_PER_PAGE } from 'src/constants/analytics';
import { formatNumberInThousand, capitalizeFirstLetter } from 'src/helpers';
import { formatFilterOptionValue } from 'src/helpers/analytics-helpers';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';

type Props = {
  listData: Array<Object>
};

type ComposedProps = Props & WithI18nProps;

type State = {
  skip: number,
  limit: number,
  sortId: string,
  isAsc: boolean,
  listData: Array<Object>
};

class Detail extends Component<ComposedProps, State> {
  state = {
    // Pagination
    skip: 0,
    limit: NUMBER_FILTER_OPTION_VALUES_PER_PAGE,

    // Sort
    sortId: 'total', //
    isAsc: false,

    // List
    listData: []
  };

  componentDidMount() {
    const { listData } = this.props;
    this.setState({ listData, skip: 0, sortId: 'total', isAsc: false });
  }

  componentWillReceiveProps(nextProps) {
    const { listData } = nextProps;
    if (this.props.listData !== listData) {
      this.setState({ listData, skip: 0, sortId: 'total', isAsc: false });
    }
  }

  renderSubject = (value, index) => {
    const { type, moneyFormat, appLanguage, i18n } = this.props;
    const { skip } = this.state;
    const numberRange = skip + index + 1;

    switch (type) {
      case 'searchTerm':
        return (
          <div>
            <span className="range">{numberRange}.</span>
            <div className="options">
              <div className="option">{value}</div>
            </div>
          </div>
        );
      case 'filterOptions':
        return (
          <div>
            <span className="range">{numberRange}.</span>
            <div className="options">
              {value.map((filterOption, filterOptionIndex) => (
                <div className="option" key={filterOptionIndex}>
                  {capitalizeFirstLetter(filterOption.key, appLanguage)}
                  <span
                    className="item-value"
                    dangerouslySetInnerHTML={{
                      __html: formatFilterOptionValue(filterOption, moneyFormat, i18n)
                    }}></span>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return '';
    }
  };

  render() {
    const { listData, sortId, isAsc, skip, limit } = this.state;

    const { compare, type, i18n } = this.props;
    const hasNext = skip + limit < listData.length;
    const hasPrevious = skip > 0;
    const displayList = _.isArray(listData) ? listData.slice(skip, skip + limit) : [];

    let firstColHeading = '',
      thirdColHeading = '';
    switch (type) {
      case 'searchTerm':
        firstColHeading = i18n.translate(
          'AdminHTML.Analytics.Display.gridDetail_SearchTerm_firstColHeading'
        );
        thirdColHeading = i18n.translate(
          'AdminHTML.Analytics.Display.gridDetail_SearchTerm_thirdColHeading'
        );
        break;
      case 'filterOptions':
        firstColHeading = i18n.translate(
          'AdminHTML.Analytics.Display.gridDetail_FilterOptions_firstColHeading'
        );
        thirdColHeading = i18n.translate(
          'AdminHTML.Analytics.Display.gridDetail_FilterOptions_thirdColHeading'
        );
        break;
      default:
    }

    return (
      <div className="content content-detail">
        <table className="Polaris-DataTable__Table">
          <thead>
            <tr>
              <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                <TableHeadingCell
                  heading={firstColHeading}
                  isAsc={isAsc}
                  idSort={sortId}
                  id={type}
                  onSort={this.onSort}
                />
              </th>
              {compare ? (
                <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header item-percentage">
                  <TableHeadingCell
                    heading={i18n.translate(
                      'AdminHTML.Analytics.Display.gridDetail_TableHeadingCell_Heading'
                    )}
                    isAsc={isAsc}
                    idSort={sortId}
                    id="percentChange"
                    onSort={this.onSort}
                  />
                </th>
              ) : (
                <th />
              )}
              <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header item-total">
                <TableHeadingCell
                  heading={thirdColHeading}
                  isAsc={isAsc}
                  idSort={sortId}
                  id="total"
                  onSort={this.onSort}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {displayList.map((item, index) => (
              <tr key={index} className="Polaris-DataTable__TableRow">
                <td className="Polaris-DataTable__Cell item-first-col">
                  {this.renderSubject(item[type], index)}
                </td>
                {compare ? (
                  <td className="Polaris-DataTable__Cell item-percentage">
                    <Percent percent={item.percentChange} />
                  </td>
                ) : (
                  <td className="Polaris-DataTable__Cell" />
                )}
                <td className={`Polaris-DataTable__Cell item-total item-${item.rowIndex}`}>
                  <TextStyle>{formatNumberInThousand(item.total)}</TextStyle>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pagination">
          {(hasPrevious || hasNext) && (
            <Pagination
              hasPrevious={hasPrevious}
              onPrevious={this.getPreviousPage}
              hasNext={hasNext}
              onNext={this.getNextPage}
            />
          )}
        </div>
      </div>
    );
  }

  // Go to previous page
  getPreviousPage = () => {
    const { skip, limit } = this.state;
    const previousSkip = skip - limit;

    // Prevent multiple click that the skip number is less than 0
    if (previousSkip < 0) return;

    // Set skip, limit for next page
    this.setState({ skip: previousSkip });
  };

  // Go to next page
  getNextPage = () => {
    const { skip, limit, listData } = this.state;
    const nextSkip = skip + limit;

    // Prevent multiple click that the skip number is bigger than total
    if (nextSkip >= listData.length) return;

    // Set skip, limit for next page and disable flag for first page loading, don't need to display skeleton loading
    this.setState({ skip: nextSkip });
  };

  // compare array object with format Array<{key, value}>
  compareItemObject = (a, b) => {
    let comparison = 0;
    for (let i = 0; i < a.length; i++) {
      const item = a[i];
      const compareItem = b[i];
      if (_.isEmpty(compareItem)) {
        comparison = 1;
        break;
      }
      const key = item.key.toLowerCase();
      const compareKey = compareItem.key.toLowerCase();
      if (key < compareKey) {
        comparison = -1;
        break;
      }
      if (key > compareKey) {
        comparison = 1;
        break;
      }

      const value = item.value.toLowerCase();
      const compareValue = compareItem.value.toLowerCase();
      if (value < compareValue) {
        comparison = -1;
        break;
      }
      if (value > compareValue) {
        comparison = 1;
        break;
      }
    }
    return comparison;
  };

  // On sort
  onSort = (idSort, isAsc) => {
    let { listData, type } = this.props;
    const asc = isAsc ? 1 : -1;
    const result = [...listData].sort((a, b) => {
      let comparison = 0;

      // Check if the id sort = filterOption -> compare Object
      if (idSort === 'filterOptions') {
        comparison = this.compareItemObject(a[idSort], b[idSort]);
      } else {
        // Compare string / number
        const aValue = typeof a[idSort] === 'string' ? a[idSort].toLowerCase() : a[idSort];
        const bValue = typeof b[idSort] === 'string' ? b[idSort].toLowerCase() : b[idSort];

        if (aValue < bValue) {
          comparison = -1;
        } else if (aValue > bValue) {
          comparison = 1;
        } else {
          // when the values are equal
          // If sort is on the first col, do nothing
          // If sort is on other col, compare the first col
          if (idSort !== 'searchTerm') {
            switch (type) {
              case 'searchTerm': {
                // Compare string
                const aValue = a[type].toLowerCase();
                const bValue = b[type].toLowerCase();
                if (aValue < bValue) {
                  comparison = -1 * asc;
                } else if (aValue > bValue) {
                  comparison = 1 * asc;
                }
                break;
              }

              case 'filterOptions': {
                // Compare object
                comparison = this.compareItemObject(a[type], b[type]) * asc;
                break;
              }
              default:
                comparison = 0;
            }
          }
        }
      }
      comparison = comparison * asc;
      return comparison;
    });
    this.setState({ listData: result, skip: 0, sortId: idSort, isAsc });
  };
}

const mapStateToProps = state => ({
  appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE)
});

export default connect(mapStateToProps)(withI18n()(Detail));
